import { CardRow } from 'components/CardRow';
import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { getChecklistOutcome } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  selectAseExplorerOptionSize,
  selectAseIsEditable,
  selectAseQuestionComment,
} from 'store/assessments/selectors';
import {
  ChecklistItem,
  ChecklistItemResult,
  Question,
  Answer,
} from 'store/assessments/types';
import { AssessmentQuestionType } from 'types/types';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { CountChips } from '../CountChips';

type Props = {
  question: Question;
  answer: Answer | undefined;
  checklistItemResults: Record<string, ChecklistItemResult | null>;
  checklistItems: ChecklistItem[];
  optionQuestionDescriptions?: 'full' | 'short';
  actionsCount: number;
  controlsCount: number;
  unreviewedActionsCount: number;
  unreviewedControlsCount: number;
};

export const ExplorerQuestionRow: React.FC<Props> = ({
  answer,
  checklistItemResults,
  checklistItems,
  optionQuestionDescriptions,
  question,
  actionsCount,
  controlsCount,
  unreviewedActionsCount,
  unreviewedControlsCount,
}) => {
  const isChecklist = question.type === AssessmentQuestionType.checklist;
  const isEditable = useSelector(selectAseIsEditable);
  const hasComment = !!useSelector((state) =>
    selectAseQuestionComment(state, question.id),
  );
  const checklistResult = isChecklist
    ? getChecklistOutcome(isEditable, checklistItems, checklistItemResults)
    : undefined;
  const explorerOptionSize = useSelector(selectAseExplorerOptionSize);

  const hasFindingsCount =
    !!actionsCount ||
    !!controlsCount ||
    !!unreviewedActionsCount ||
    !!unreviewedControlsCount;

  return (
    <CardRow
      childrenStyle={{ flexDirection: 'row' }}
      icon={isChecklist ? ICONS.CircleCSolid : ICONS.CircleQSolid}
      iconColor={isChecklist ? theme.colors.checklist : theme.colors.question}
      style={{ backgroundColor: 'transparent' }}
      compact={explorerOptionSize === 'small'}
      small={explorerOptionSize === 'small'}
    >
      <View style={{ flex: 1 }}>
        <Txt
          size={explorerOptionSize === 'small' ? 'xsmall' : undefined}
          numberOfLines={optionQuestionDescriptions === 'short' ? 2 : undefined}
        >
          {question.question}
        </Txt>
      </View>
      {(hasComment ||
        hasFindingsCount ||
        answer ||
        (isChecklist && checklistResult)) && (
        <ViewWithSeparators
          horizontal
          style={{
            marginLeft: theme.paddingHorizontal,
            alignItems: 'center',
          }}
          separatorMargin={theme.paddingHorizontal / 2}
        >
          {hasComment && (
            <Icon
              source={ICONS.CommentLight}
              color={theme.colors.fieldValueText}
              style={{ marginBottom: -1 }}
            />
          )}
          {hasFindingsCount && (
            <CountChips
              questions={0}
              checklists={0}
              checklistsCompleted={0}
              questionsCompleted={0}
              actions={actionsCount}
              controls={controlsCount}
              unreviewedActions={unreviewedActionsCount}
              unreviewedControls={unreviewedControlsCount}
            />
          )}
          {answer && (
            <View style={styles.resultContainer}>
              <View
                style={[
                  styles.answer,
                  {
                    backgroundColor: answer.negative
                      ? theme.colors.red10
                      : theme.colors.background,
                  },
                ]}
              >
                <Txt
                  weight="bold"
                  size="xsmall"
                  numberOfLines={2}
                  style={[
                    styles.answerText,
                    answer.negative && styles.answerTextNegative,
                  ]}
                >
                  {answer.answer}
                </Txt>
              </View>
            </View>
          )}
          {isChecklist && checklistResult && (
            <View style={styles.resultContainer}>
              <View
                style={[
                  styles.checklistItemsResult,
                  {
                    backgroundColor: checklistResult.color2,
                  },
                ]}
              >
                {checklistResult.percentage < 100 && (
                  <View
                    style={{
                      position: 'absolute',
                      bottom: 3,
                      left: 3,
                      right: 3,
                      overflow: 'hidden',
                      height: 3,
                      borderRadius: 1.5,
                      backgroundColor: checklistResult.color3,
                    }}
                  >
                    <View
                      style={{
                        height: 3,
                        borderRadius: 1.5,
                        width: `${checklistResult.percentage}%`,
                        backgroundColor: checklistResult.color,
                      }}
                    />
                  </View>
                )}
                <Icon
                  source={checklistResult.icon}
                  color={checklistResult.color}
                  style={{
                    marginTop: checklistResult.percentage < 100 ? -4 : 0,
                  }}
                />
              </View>
            </View>
          )}
        </ViewWithSeparators>
      )}
    </CardRow>
  );
};

const styles = StyleSheet.create({
  resultContainer: {
    backgroundColor: 'white',
    borderRadius: 4,
    marginVertical: -11,
  },
  answer: {
    borderRadius: 4,
    padding: 4,
    minWidth: 33,
    minHeight: 33,
    alignItems: 'center',
    justifyContent: 'center',
  },
  answerText: {
    maxWidth: 70,
    textAlign: 'center',
  },
  answerTextNegative: {
    color: theme.colors.red,
  },
  checklistItemsResult: {
    backgroundColor: theme.colors.red10,
    width: 33,
    height: 33,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
